<template>
  <div class="section py-0 px-5">
    <div>
      <b-tabs v-model="activeTab" position="is-centered is-small"
        class="block tabs-side" type="is-toggle" expanded>
        <b-tab-item icon="map-marker" class="is-primary"/>
        <b-tab-item icon="account-circle" class="is-primary"/>
        <b-tab-item icon="store" class="is-primary" :disabled="!hasGeoMarketing"/>
        <b-tab-item v-if="isRouteFaceUnitaire || isRoutePanneauNumerique"
          icon="barcode" class="is-primary"/>
      </b-tabs>
    </div>
    <div v-if="activeTab === 0">
      <div class="columns mb-0">
        <div class="column" :class="{'pr-0': btnAdd}">
          <gmap-autocomplete @place_changed="miseAJourCoordonneeAdresse" selectFirstOnEnter
            class="input" type="search"
            v-model="libelleAdresse"
            :options="getBounds()"
            />
        </div>
        <div class="column is-3" v-if="btnAdd && !newsPoint.length">
          <a class="button is-primary"
             @click="addPoint">
            <b-icon icon="plus"></b-icon>
          </a>
        </div>
        <div class="column is-3" v-if="btnAdd && newsPoint.length">
          <a class="button is-danger"
             @click="subtractPoint(0)">
            <b-icon icon="delete"></b-icon>
          </a>
        </div>
      </div>
      <div class="columns mb-0"
           v-for="(point, i) in newsPoint"
           :key="i">
        <div class="column pr-0">
          <gmap-autocomplete @place_changed="miseAJourCoordonneePoint($event, i)" selectFirstOnEnter
            class="input" type="search"
            v-model="point.libelle"
            :options="getBounds()"
            />
        </div>
        <div class="column is-3" v-if="i === newsPoint.length - 1">
          <a class="button is-primary"
             @click="addPoint">
            <b-icon icon="plus"></b-icon>
          </a>
        </div>
        <div class="column is-3" v-else>
          <a class="button is-danger"
             @click="subtractPoint(i)">
            <b-icon icon="delete"></b-icon>
          </a>
        </div>
      </div>
    </div>
    <div v-if="activeTab === 1">
      <b-autocomplete
        v-model="nomClient"
        type="search"
        placeholder="Indiquez un client"
        :keep-first="keepFirst"
        :open-on-focus="openOnFocus"
        :data="dataClients"
        field="libelleRecherche"
        @select="searchClient"
        @input="getAsyncClientData"
        :loading="isFetching" ref="element" :closable="false" append-to-body>
        <template slot="empty">Aucun résultat trouvé, saisir au moins 3 caractères</template>
        <template slot-scope="props">
          <div>
            <div class="has-text-weight-bold">
              {{ props.option.nomCommercial }}
            </div>
            <small>
              <em>{{ props.option.ville }}</em>
              <br>
              <span class="has-text-warning has-text-weight-bold"
                v-if="props.option.blocageLevel === levelComptant">
                {{ levelToString(props.option.blocageLevel) }}
              </span>
              <span class="has-text-danger has-text-weight-bold"
                v-if="props.option.blocageLevel === levelTotal">
                {{ levelToString(props.option.blocageLevel) }}
              </span>
            </small>
          </div>
        </template>
      </b-autocomplete>
    </div>
    <div v-if="activeTab === 2">
      <b-field>
        <b-autocomplete
        v-model="nomCommuneLSA"
        type="search"
        placeholder="Indiquez une ville"
        :keep-first="keepFirstCommuneLSA"
        :open-on-focus="openOnFocusCommuneLSA"
        :data="dataCommunesLSA"
        field="libelleRecherche"
        @select="searchCommuneLSA"
        @input="getAsyncCommuneLSAData"
        :loading="isFetchingCommuneLSA" ref="elementCommuneLSA" :closable="false">
        <template slot="empty">Aucun résultat trouvé, saisir au moins 3 caractères</template>
      </b-autocomplete>
      </b-field>
      <b-field>
        <b-autocomplete
          v-model="clientLSA"
          type="search"
          placeholder="Indiquez une enseigne"
          :keep-first="keepFirst"
          :open-on-focus="openOnFocus"
          :data="filteredDataClientsLSA"
          :disabled="dataClientsLSA.length === 0"
          field="libelleRecherche"
          @select="searchClientLSA"
          :closable="false">
          <template slot="empty">Aucun résultat trouvé, saisir au moins 3 caractères</template>
          <template slot-scope="props">
            <div>
              {{ props.option.enseigne }}
              <br>
              <small>
                  <em>{{ props.option.adresse }}</em>
              </small>
            </div>
          </template>
        </b-autocomplete>
      </b-field>
    </div>
    <div v-if="activeTab === 3">
      <b-field>
        <b-input
        v-model="codeFace"
        placeholder="Indiquez un code face"
        @keyup.enter.native="enterClicked">
        <template slot="empty">Aucun résultat trouvé, saisir au moins 3 caractères</template>
      </b-input>
      </b-field>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { iconColorGetter, TAILLE_ICON_PETIT } from '@/services/general/iconColorGetter';
import adresseGeoCoder from '@/services/general/adresseGeoCoder';
import * as typesPanier from '@/store/creationOpportunite/panier-mutation-type';
import * as typesVenteFaceLc from '@/store/creationOpportunite/venteFacesLc-mutation-type';
import gestionErreur from '@/services/general/gestionErreur';
import debounce from 'lodash/debounce';
import { browseClientsByQuery } from '@/api/clients/index';
import { browseCommunes } from '@/api/geoGouvFr/index';
import { getLSA } from '@/api/asterop/index';
import { DEBOUNCE } from '@/configuration/autocompleteConf';
import * as clientConfig from '@/configuration/clientConf';
import { LIMITE_AU_PAIEMENT_COMPTANT, TOTAL } from '@/configuration/clientConf';
import levelString from '@/services/client/levelToString';

const Panier = createNamespacedHelpers('panier');
const Client = createNamespacedHelpers('client');
const User = createNamespacedHelpers('user');
const VenteFacesLc = createNamespacedHelpers('venteFacesLc');

function cleanString(chaineEntre) {
  let chaine = chaineEntre.replace(/é/g, 'e');
  chaine = chaine.replace(/è/g, 'e');
  chaine = chaine.replace(/ë/g, 'e');
  chaine = chaine.replace(/ê/g, 'e');
  chaine = chaine.replace(/à/g, 'a');
  chaine = chaine.replace(/â/g, 'a');
  chaine = chaine.replace(/á/g, 'a');
  chaine = chaine.replace(/ï/g, 'i');
  chaine = chaine.replace(/ì/g, 'i');
  chaine = chaine.replace(/î/g, 'i');
  chaine = chaine.replace(/ò/g, 'o');
  chaine = chaine.replace(/ô/g, 'o');
  chaine = chaine.replace(/ö/g, 'o');
  chaine = chaine.replace(/õ/g, 'o');
  chaine = chaine.replace(/ñ/g, 'n');
  chaine = chaine.replace(/ń/g, 'n');
  chaine = chaine.replace(/\{/g, '');
  chaine = chaine.replace(/\}/g, '');
  chaine = chaine.replace(/\[/g, '');
  chaine = chaine.replace(/\]/g, '');
  chaine = chaine.replace(/\)/g, '');
  chaine = chaine.replace(/\(/g, '');
  chaine = chaine.replace(/!/g, '');
  chaine = chaine.replace(/"/g, '');
  chaine = chaine.replace(/,/g, '');
  chaine = chaine.replace(/\$/g, '');
  chaine = chaine.replace(/\^/g, '');
  chaine = chaine.replace(/&/g, '');
  chaine = chaine.replace(/€/g, '');
  return chaine;
}

export default {
  name: 'autocompleteClient',
  props: {
    btnAdd: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      nomCommuneLSA: '',
      communeLSASelected: null,
      dataClients: [],
      keepFirstCommuneLSA: true,
      openOnFocusCommuneLSA: false,
      dataCommunesLSA: [],
      isFetchingCommuneLSA: false,
      dataClientsLSA: [],
      isFetching: false,
      clientLSA: '',
      loadingComponent: false,
      coordonnees: null,
      place: '',
      keepFirst: true,
      openOnFocus: false,
      nomClient: '',
      valeurMax: 180,
      libelleAdresse: '',
      isLoading: true,
      codeFace: '',
      newsPoint: [],
      levelComptant: LIMITE_AU_PAIEMENT_COMPTANT,
      levelTotal: TOTAL,
    };
  },
  computed: {
    ...Panier.mapGetters({
      getClientPanier: 'clientPanier',
      getAdressePanier: 'adressePanier',
      getActiveTab: 'activeTab',
    }),
    ...Client.mapGetters({
      client: 'client',
    }),
    ...User.mapGetters({
      hasGeoMarketing: 'hasGeoMarketing',
    }),
    filteredDataClientsLSA() {
      return this.dataClientsLSA.filter((option) => {
        return option.libelleRecherche
          .toString()
          .toLowerCase()
          .indexOf(this.clientLSA.toLowerCase()) >= 0;
      });
    },
    adressePanier: {
      get() {
        return this.getAdressePanier;
      },
      set(value) {
        this.setAdressePanier(value);
      },
    },
    clientPanier: {
      get() {
        return this.getClientPanier;
      },
      set(value) {
        this.setContactPanier(null);
        this.setClientPanier(value);
      },
    },
    activeTab: {
      get() {
        return this.getActiveTab;
      },
      set(value) {
        return this.setActiveTab(value);
      },
    },
    isRoutePanneauNumerique: {
      get() {
        return this.$route.name === 'vente-ecrans-numeriques';
      },
    },
    isRouteFaceUnitaire: {
      get() {
        return this.$route.name === 'vente-faces-LC';
      },
    },
  },
  watch: {
    activeTab(value) {
      let afficherSlider = true;
      if (value === 3) {
        this.searchByCode(true);
        afficherSlider = false;
      } else {
        this.searchByCode(false);
      }
      this.$emit('afficherSlider', afficherSlider);
    },
  },
  methods: {
    ...Panier.mapMutations({
      setContactPanier: typesPanier.SET_CONTACT_PANIER,
      setClientPanier: typesPanier.SET_CLIENT_PANIER,
      setAdressePanier: typesPanier.SET_ADRESSE_PANIER,
      setActiveTab: typesPanier.SET_ACTIVE_TAB,
    }),
    ...Client.mapActions({
      actionsRechercheDetailClient: 'rechercheDetailClient',
    }),
    ...VenteFacesLc.mapMutations({
      searchByCode: typesVenteFaceLc.SET_SEARCH_BY_CODE,
    }),
    /* eslint-disable-next-line */
    getAsyncClientData: debounce(function () {
      this.dataClients = [];
      if (this.nomClient.length <= 2) return;
      this.isFetching = true;
      browseClientsByQuery(this.nomClient, clientConfig.TYPE_CLIENT,
        clientConfig.SOUS_TYPE).then((response) => {
        response.data.forEach((client) => {
          this.dataClients.push(client);
        });
        this.isFetching = false;
      }).catch(() => {
        this.isFetching = false;
      });
    }, DEBOUNCE),
    /* eslint-disable-next-line */
    getAsyncCommuneLSAData: debounce(function () {
      this.dataCommunesLSA = [];
      if (!this.communeLSASelected) {
        this.dataClientsLSA = [];
        this.selectedClientLSA = null;
        this.clientLSA = '';
      }
      if (this.nomCommuneLSA.length <= 2) return;
      this.isFetchingCommuneLSA = true;
      browseCommunes(this.nomCommuneLSA).then((response) => {
        response.data.forEach((client) => {
          client.libelleRecherche = `${client.nom} (${client.codeDepartement})`;
          this.dataCommunesLSA.push(client);
        });
        this.isFetchingCommuneLSA = false;
      }).catch(() => {
        this.isFetchingCommuneLSA = false;
      });
    }, DEBOUNCE),
    async searchClient(clientSelect) {
      if (!clientSelect) {
        this.coordonnees = null;
        return;
      }
      const loadingComponent = this.$buefy.loading.open({ container: null });
      await this.actionsRechercheDetailClient({ cliNum: clientSelect.num });
      if (this.client == null) {
        loadingComponent.close();
        return;
      }
      this.clientPanier = this.client;
      // Vérification si la latitude et la longitude sont comprises entre -180 et 180
      // Certains clients ont renseigné les coordonnées avec des virgules au lieu de points
      if (this.clientPanier.latitude &&
          this.clientPanier.longitude &&
          this.valeurMax >= Math.abs(this.clientPanier.latitude) &&
          this.valeurMax >= Math.abs(this.clientPanier.longitude)) {
        this.coordonnees = {
          lat: this.clientPanier.latitude,
          lng: this.clientPanier.longitude,
          urlLogo: this.clientPanier.urlLogo,
        };
        this.clientPanier.coordonnees = this.coordonnees;
        this.envoieCoordonnees();
        loadingComponent.close();
        return;
      }
      this.searchCoordinatesByAddress(this.clientPanier);
      loadingComponent.close();
    },
    searchClientLSA(clientSelect) {
      if (!clientSelect) {
        this.coordonnees = null;
        return;
      }
      this.coordonnees = {
        lat: clientSelect.coordinates[1],
        lng: clientSelect.coordinates[0],
        symbole: clientSelect.symbole,
        codeType: clientSelect.code_type,
      };
      this.adressePanier = {
        coordonnees: this.coordonnees,
        libelleAdresse: clientSelect.adresse,
      };
      this.searchCoordinatesByAddressLSA(clientSelect);
    },
    async searchCommuneLSA(communeSelect) {
      if (!communeSelect) {
        this.communeLSASelected = null;
        return;
      }
      this.communeLSASelected = communeSelect;
      this.isFetchingCommuneLSA = true;
      const response = await getLSA(this.communeLSASelected.code);
      if (Object.prototype.hasOwnProperty.call(response.data, 'stores') &&
          response.data.stores.length > 0) {
        this.dataClientsLSA = response.data.stores.map(item => {
          item.libelleRecherche = `${item.enseigne}`;
          return item;
        });
      } else {
        gestionErreur('Erreur d\'accès à l\'API LSA.');
      }
      this.isFetchingCommuneLSA = false;
    },
    searchCoordinatesByAddressLSA(data) {
      const client = {
        adresse: data.adresse,
        coordinates: data.coordinates,
        nomCommercial: data.libelleRecherche,
        nomCourt: data.enseigne,
        num: null,
      };
      const address = client.adresse;
      adresseGeoCoder(address).then((position) => {
        client.latitude = position.lat();
        client.longitude = position.lng();
        this.coordonnees.lat = client.latitude;
        this.coordonnees.lng = client.longitude;
        client.coordonnees = this.coordonnees;
        this.clientPanier = client;
        this.envoieCoordonnees();
      }).catch((e) => {
        gestionErreur('Impossible de géolocaliser le client. Veuillez utiliser la fonctionnalité géolocalisation dans Affixe1.', e);
      });
    },
    searchCoordinatesByAddress(client) {
      const address = client.adrLibelle1 + ' ' + client.adrCp + ' ' + client.adrVille;
      adresseGeoCoder(address).then((position) => {
        client.latitude = position.lat();
        client.longitude = position.lng();
        this.coordonnees = {
          lat: client.latitude,
          lng: client.longitude,
        };
        client.coordonnees = this.coordonnees;
        this.clientPanier = client;
        this.envoieCoordonnees();
      }).catch((e) => {
        gestionErreur('Impossible de géolocaliser le client. Veuillez utiliser la fonctionnalité géolocalisation dans Affixe1.', e);
      });
    },
    miseAJourCoordonneeAdresse(place) {
      if (!place.geometry) {
        let messErreur = 'Pas de données géographique pour l\'adresse';
        if (place.name) messErreur = messErreur + ': ' + place.name;
        this.adressePanier = null;
        gestionErreur(messErreur);
        return;
      }
      this.coordonnees = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        libelleAdresse: place.name,
      };
      this.adressePanier = {
        coordonnees: this.coordonnees,
        libelleAdresse: place.name + ', ' + place.vicinity,
      };
      this.envoieCoordonnees();
    },
    envoieCoordonnees() {
      // Cleanup
      switch (this.activeTab) {
        case 0:
        case 1:
          this.communeLSASelected = null;
          this.nomCommuneLSA = '';
          this.dataClientsLSA = [];
          this.selectedClientLSA = null;
          this.clientLSA = '';
          break;
        default:
          break;
      }
      if (this.coordonnees) {
        this.$emit('coordonneesChange', this.coordonnees);
      }
    },
    enterClicked() {
      this.codeFace = cleanString(this.codeFace);
      this.codeFace = this.codeFace.trim();
      this.codeFace = this.codeFace.toUpperCase();
      this.$emit('codeChange', this.codeFace);
    },
    addPoint() {
      this.newsPoint.push({});
    },
    subtractPoint(index) {
      this.newsPoint.splice(index, 1);
      this.$emit('sendPoints', this.newsPoint);
    },
    miseAJourCoordonneePoint(place, index) {
      if (!place.geometry) {
        let messErreur = 'Pas de données géographique pour l\'adresse';
        if (place.name) messErreur = messErreur + ': ' + place.name;
        gestionErreur(messErreur);
        return;
      }
      this.newsPoint.splice(index, 1, {
        libelle: place.name + ', ' + place.vicinity,
        coordonnees: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
        icon: iconColorGetter('#000000', TAILLE_ICON_PETIT),
      });
      this.$emit('sendPoints', this.newsPoint);
    },
    levelToString(blocageLevel) {
      return levelString(blocageLevel);
    },
  },
  mounted() {
    let afficherSlider = true;
    if (this.activeTab === 3) {
      this.searchByCode(true);
      afficherSlider = false;
    } else {
      this.searchByCode(false);
    }
    this.$emit('afficherSlider', afficherSlider);
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/filtres/autocompleteClient.vue